import { Component } from '@theme/component';
import { API_ENDPOINTS } from '@theme/api-endpoints';
import { trackFormSubmit } from '@theme/analytics';

/**
 * <contact-us> — Global Contact Us component.
 *
 * Hosts BOTH forms on the Contact Us page:
 *   - General Customer Support form (always present)
 *   - Shopping Consultant form (present only for logged-in UFO/PPC users)
 *
 * Both forms POST JSON to the portal-email app-proxy endpoint.
 * On success: clears inputs, shows success message below the form, keeps form visible.
 * On error: shows API error below the form, preserves entered values.
 *
 * Consultant refs are optional — they only exist when the consultant block renders,
 * so they are intentionally left out of requiredRefs.
 */

/** @type {RegExp} Basic email validation */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** How long a submit confirmation stays on screen, in milliseconds. */
const CONFIRMATION_DURATION = 3000;

/**
 * Clean API error field prefixes for user-friendly display.
 * e.g. "fromEmail is required" → "Email is required"
 * @param {string} message
 * @returns {string}
 */
const cleanErrorMessage = (message) => {
  if (!message) return '';
  return message
    .replace(/\bfromEmail\b/gi, 'Email')
    .replace(/\btoEmail\b/gi, 'Recipient email')
    .replace(/\bportalID\b/gi, 'Portal ID')
    .replace(/\breadTerms\b/gi, 'Terms');
};

class ContactUs extends Component {
  requiredRefs = [
    'supportName',
    'supportEmail',
    'supportPhone',
    'supportDescription',
    'supportSubmitBtn',
    'supportErrorEl',
    'supportSuccessEl',
    'supportSuccessText',
    'supportFormWrap',
    'supportToEmail',
    'supportPortalID',
  ];

  /**
   * Validate email format.
   * @param {string} email
   * @returns {boolean}
   */
  #isValidEmail(email) {
    return EMAIL_REGEX.test(email);
  }

  /**
   * Whether a recipient address has been set up — present and well-formed.
   * Blank means the store/portal email metafield is missing, so the message
   * could never be delivered.
   * @param {string} toEmail
   * @returns {boolean}
   */
  #hasRecipient(toEmail) {
    return Boolean(toEmail) && this.#isValidEmail(toEmail);
  }

  /**
   * Show an error message in the given error element.
   *
   * Errors stay on screen until the next submit attempt, unlike the confirmation.
   * An error is an instruction — it says what to correct, and it is needed while the
   * visitor is correcting it. Both forms report every failure through a single shared
   * line with no per-field marking, so timing it out would leave someone who looked
   * away with no indication of which of the required fields was the problem.
   *
   * Nothing extra is needed to clear it: the next failed attempt replaces this text,
   * and the next successful one calls #hideError before posting.
   *
   * @param {HTMLElement} errorEl
   * @param {string} message
   */
  #showError(errorEl, message) {
    errorEl.textContent = message;
    errorEl.classList.remove('tw:hidden');
  }

  /**
   * Hide the given error element.
   * @param {HTMLElement} errorEl
   */
  #hideError(errorEl) {
    errorEl.textContent = '';
    errorEl.classList.add('tw:hidden');
  }

  /**
   * POST a form payload to the portal-email endpoint and update the UI.
   * @param {Object} payload
   * @param {Object} ui — { formEl, fields, submitBtn, errorEl, successEl, successText }
   */
  async #submit(payload, ui) {
    const { formEl, fields, submitBtn, errorEl, successEl, successText } = ui;

    try {
      const response = await fetch(API_ENDPOINTS.contactPortalEmail, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data?.meta_data?.status === 'success') {
        // Not deduped: the form stays usable after the confirmation (see below),
        // so a second accepted message is a second submission.
        trackFormSubmit(formEl);

        // Success — clear form fields
        for (const field of fields) {
          if (field) field.value = '';
        }

        // Both confirmations share one pinned position, so clear any that is still
        // showing rather than stacking the second one on top of the first.
        this.#hideConfirmations();

        // The confirmation is pinned to the viewport instead of sitting after the form.
        // Either form is tall enough that an in-flow message lands below the fold, which
        // is what made a successful submit look like nothing had happened. The form stays
        // where it is and stays usable, so another message can be sent straight away.
        successText.textContent = data?.meta_data?.message_detail || 'Your message has been sent.';
        successEl.classList.remove('tw:hidden');
        successEl.classList.add('tw:flex');
        setTimeout(() => {
          successEl.classList.add('tw:hidden');
          successEl.classList.remove('tw:flex');
        }, CONFIRMATION_DURATION);

        submitBtn.disabled = false;
      } else {
        const errorMsg = data?.meta_data?.message_detail
          ? cleanErrorMessage(data.meta_data.message_detail)
          : this.dataset.errorMessage || 'Something went wrong. Please try again.';
        this.#showError(errorEl, errorMsg);
        submitBtn.disabled = false;
      }
    } catch {
      this.#showError(errorEl, this.dataset.errorMessage || 'Something went wrong. Please try again.');
      submitBtn.disabled = false;
    }
  }

  /** Hide every confirmation in this section, whichever form produced it. */
  #hideConfirmations() {
    for (const el of this.querySelectorAll('.contact-us__confirmation')) {
      el.classList.add('tw:hidden');
      el.classList.remove('tw:flex');
    }
  }

  /**
   * Wire up Shopify advanced captcha if present, then run the callback.
   * @param {HTMLFormElement} formEl
   * @param {() => void} callback
   */
  #withCaptcha(formEl, callback) {
    if (window.Shopify?.recaptchaV3?.execute) {
      window.Shopify.recaptchaV3.execute(formEl, callback);
    } else {
      callback();
    }
  }

  /**
   * Handle General Customer Support form submission.
   * @param {SubmitEvent} event
   */
  handleSupportSubmit = async (event) => {
    event.preventDefault();

    const formEl = event.target.closest('form');
    const {
      supportName,
      supportEmail,
      supportPhone,
      supportSubject,
      supportOrderNumber,
      supportDescription,
      supportSubmitBtn,
      supportErrorEl,
      supportSuccessEl,
      supportSuccessText,
      supportToEmail,
      supportPortalID,
    } = this.refs;

    const email = supportEmail.value.trim();
    const subject = supportSubject ? supportSubject.value.trim() : '';
    const description = supportDescription.value.trim();
    const toEmail = supportToEmail ? supportToEmail.value.trim() : '';

    // Without a recipient the message can never be delivered — fail early
    if (!this.#hasRecipient(toEmail)) {
      this.#showError(
        supportErrorEl,
        this.dataset.validationToEmailMissing || 'This form is not set up to receive messages yet.'
      );
      return;
    }

    // Validate required fields
    if (!email) {
      this.#showError(supportErrorEl, this.dataset.validationEmailRequired || 'Email is required.');
      return;
    }
    if (!this.#isValidEmail(email)) {
      this.#showError(supportErrorEl, this.dataset.validationEmailInvalid || 'Please enter a valid email address.');
      return;
    }
    if (!subject) {
      this.#showError(supportErrorEl, this.dataset.validationSubjectRequired || 'Subject is required.');
      return;
    }
    if (!description) {
      this.#showError(supportErrorEl, this.dataset.validationDescriptionRequired || 'Description is required.');
      return;
    }

    supportSubmitBtn.disabled = true;
    this.#hideError(supportErrorEl);

    // Hide any previously visible success message
    supportSuccessEl.classList.add('tw:hidden');
    supportSuccessEl.classList.remove('tw:flex');

    const orderNumber = supportOrderNumber ? supportOrderNumber.value.trim() : '';
    const payload = {
      portalID: supportPortalID ? supportPortalID.value : '',
      mailFromName: supportName.value.trim(),
      toEmail,
      fromEmail: email,
      phone: supportPhone.value.trim(),
      subject,
      orderID: orderNumber,
      message: description,
      readTerms: 1,
    };

    this.#withCaptcha(formEl, () =>
      this.#submit(payload, {
        formEl,
        fields: [supportName, supportEmail, supportPhone, supportSubject, supportOrderNumber, supportDescription],
        submitBtn: supportSubmitBtn,
        errorEl: supportErrorEl,
        successEl: supportSuccessEl,
        successText: supportSuccessText,
      })
    );
  };

  /**
   * Handle Shopping Consultant form submission.
   * @param {SubmitEvent} event
   */
  handleConsultantSubmit = async (event) => {
    event.preventDefault();

    const formEl = event.target.closest('form');
    const {
      consultantName,
      consultantEmail,
      consultantPhone,
      consultantSubject,
      consultantMessage,
      consultantSubmitBtn,
      consultantErrorEl,
      consultantSuccessEl,
      consultantSuccessText,
      consultantToEmail,
      consultantPortalID,
    } = this.refs;

    const name = consultantName.value.trim();
    const email = consultantEmail.value.trim();
    const phone = consultantPhone.value.trim();
    const subject = consultantSubject.value.trim();
    const message = consultantMessage.value.trim();
    const toEmail = consultantToEmail ? consultantToEmail.value.trim() : '';

    // Without a recipient the message can never be delivered — fail early
    if (!this.#hasRecipient(toEmail)) {
      this.#showError(
        consultantErrorEl,
        this.dataset.validationToEmailMissing || 'This form is not set up to receive messages yet.'
      );
      return;
    }

    // Validate all required fields
    if (!name) {
      this.#showError(consultantErrorEl, this.dataset.validationNameRequired || 'Name is required.');
      return;
    }
    if (!email) {
      this.#showError(consultantErrorEl, this.dataset.validationEmailRequired || 'Email is required.');
      return;
    }
    if (!this.#isValidEmail(email)) {
      this.#showError(consultantErrorEl, this.dataset.validationEmailInvalid || 'Please enter a valid email address.');
      return;
    }
    if (!phone) {
      this.#showError(consultantErrorEl, this.dataset.validationPhoneRequired || 'Phone number is required.');
      return;
    }
    if (!subject) {
      this.#showError(consultantErrorEl, this.dataset.validationSubjectRequired || 'Subject is required.');
      return;
    }
    if (!message) {
      this.#showError(consultantErrorEl, this.dataset.validationMessageRequired || 'Message is required.');
      return;
    }

    consultantSubmitBtn.disabled = true;
    this.#hideError(consultantErrorEl);

    // Hide any previously visible success message
    consultantSuccessEl.classList.add('tw:hidden');
    consultantSuccessEl.classList.remove('tw:flex');

    const payload = {
      portalID: consultantPortalID ? consultantPortalID.value : '',
      mailFromName: name,
      toEmail,
      fromEmail: email,
      phone,
      subject,
      message,
      readTerms: 1,
    };

    this.#withCaptcha(formEl, () =>
      this.#submit(payload, {
        formEl,
        fields: [consultantName, consultantEmail, consultantPhone, consultantSubject, consultantMessage],
        submitBtn: consultantSubmitBtn,
        errorEl: consultantErrorEl,
        successEl: consultantSuccessEl,
        successText: consultantSuccessText,
      })
    );
  };
}

if (!customElements.get('contact-us')) {
  customElements.define('contact-us', ContactUs);
}
